'use client';

import { useRef, SetStateAction, useEffect, useState, LegacyRef } from 'react';
import Webcam from 'react-webcam';

import { checkIfIsMobileBrowser } from '@northladder/utilities';

import { useViewportZoomLock } from '../../hooks';

import {
  ButtonCancelCamera,
  ButtonCaptureImage,
  CameraLoader,
} from './components';

interface ICameraRef {
  takePhoto: () => SetStateAction<string>;
}

interface ICameraCaptureProps {
  /**
   * Determines the quality of the image captured based on how the webcam captured
   * screenshots is compressed. On a scale of 0 to 1, where 0 is lowest quality.
   * @default 0.9
   */
  imageQuality?: number;
  initHeight?: number;
  initWidth?: number;
  onDismissCamera: () => void;
  onUpdateCapturedImage: (imageURL: string) => void;
}

/**
 * -----------------------------------------------------------------------------
 * This renders a camera element that loads a device camera and provides options
 * to capture the image.
 * NOTE: This is alternative to the `CameraCapture` shown in screen.
 */
export function CameraCaptureAlt({
  imageQuality = 0.9,
  initHeight = window.outerHeight,
  initWidth = window.outerWidth,
  onDismissCamera,
  onUpdateCapturedImage,
}: ICameraCaptureProps) {
  const camera = useRef<ICameraRef>(null);
  const [isCameraLoading, setIsCameraLoading] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  const webcamRef = useRef<Webcam>(null);
  const wrapperRef: LegacyRef<HTMLDivElement> = useRef(null);
  const [parentSize, setParentSize] = useState({
    width: initWidth,
    height: initHeight,
  });

  useViewportZoomLock();

  const handleCaptureShot = () => {
    if (webcamRef.current) {
      setIsProcessing(() => true);
      const imageSrc = webcamRef.current.getScreenshot();

      if (imageSrc) {
        onUpdateCapturedImage(imageSrc);
      }
    }
  };

  useEffect(() => {
    const handleResize = () => {
      if (wrapperRef?.current) {
        const parent = wrapperRef.current;

        setParentSize({
          width: parent.clientWidth,
          height: parent.clientHeight,
        });
      }
    };

    // Initial size calculation
    handleResize();
    setIsProcessing(() => false);

    // Listen for window resize events
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [webcamRef]);

  const { width } = parentSize;
  const { height } = parentSize;

  const handleCameraLoaded = () => {
    setIsCameraLoading(false);
  };

  useEffect(() => {
    setIsMobile(checkIfIsMobileBrowser());
  }, []);

  // NOTE: This is a hack to fix the camera view on mobile devices.
  const viewWidth = isMobile ? window.innerWidth : width;
  const viewHeight = isMobile ? window.innerHeight : height;

  return (
    <div
      className="
        crop-image-width border-primary-100 flex h-[calc(100vh-6rem)]
        max-h-[1000px] flex-col overflow-hidden rounded-xl border-2 bg-white/80 sm:max-h-[800px]
        sm:w-full md:h-[calc(100vh-10rem)]
      "
    >
      {isCameraLoading ? <CameraLoader className="top-20" /> : null}

      {camera ? (
        <div
          ref={wrapperRef}
          className="relative h-full w-full overflow-hidden"
        >
          {wrapperRef.current ? (
            <Webcam
              ref={webcamRef}
              audio={false}
              minScreenshotHeight={viewHeight}
              minScreenshotWidth={viewWidth}
              onUserMedia={handleCameraLoaded}
              screenshotFormat="image/jpeg"
              screenshotQuality={imageQuality}
              videoConstraints={{
                facingMode: 'environment',
                width: viewHeight,
              }}
            />
          ) : null}
        </div>
      ) : null}

      <div
        className="
          relative bottom-0 left-0 z-10 flex w-full justify-between gap-2 rounded-lg
          p-2 sm:mt-1
        "
      >
        <ButtonCancelCamera onClick={onDismissCamera} />
        <ButtonCaptureImage
          isProcessing={isProcessing}
          onClick={handleCaptureShot}
        />
      </div>
    </div>
  );
}
